import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_hasFireplace,
  useRentalManagerUserPropertyEditWebApiHandler_hasPatioBalcony,
  useRentalManagerUserPropertyEditWebApiHandler_isFurnished,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { OTHER_UNIT_KEYS, OTHER_UNIT_METADATA, type OtherUnitKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<OtherUnitKey, boolean>;

const OtherUnitAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const buildingAttributes = listingDetails?.buildingAttributes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasFireplace: buildingAttributes?.hasFireplace ?? false,
    hasPatioBalcony: buildingAttributes?.hasPatioBalcony ?? false,
    isFurnished: buildingAttributes?.isFurnished ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Other amenities updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateFireplace } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasFireplace,
    skipToast: true,
    meta: { context: { api: 'hasFireplace', details: { listingAlias } } },
  });

  const { mutateAsync: updatePatioBalcony } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPatioBalcony,
    skipToast: true,
    meta: { context: { api: 'hasPatioBalcony', details: { listingAlias } } },
  });

  const { mutateAsync: updateFurnished } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_isFurnished,
    skipToast: true,
    meta: { context: { api: 'isFurnished', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateFireplace> = {
    hasFireplace: updateFireplace,
    hasPatioBalcony: updatePatioBalcony,
    isFurnished: updateFurnished,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as OtherUnitKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = OTHER_UNIT_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Other</Legend>} disabled={isInputFormDisabled}>
      {OTHER_UNIT_KEYS.map((fieldName) => {
        const metadata = OTHER_UNIT_METADATA[fieldName];
        return (
          <LabeledControl
            key={`otherTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(OtherUnitAmenities);
